import React from 'react';
import { Bell, Vote, UserPlus, FileText, Image as ImageIcon, Wifi, WifiOff, CheckCheck, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';

const EVENT_META = {
  new_vote: {
    icon: Vote,
    color: '#D4AF37',
    title: (p) => 'New vote cast',
    desc: (p) => `${p.voter_name || 'Someone'} voted for ${p.nominee_name || 'a nominee'}`,
  },
  new_submission: {
    icon: FileText,
    color: '#3B82F6',
    title: (p) => 'New registration',
    desc: (p) => `${p.user_name || 'Someone'} registered for ${p.event_title || 'an event'}`,
  },
  new_member: {
    icon: UserPlus,
    color: '#22C55E',
    title: (p) => 'New member joined',
    desc: (p) => `${p.name || 'A new member'} signed up`,
  },
  new_media: {
    icon: ImageIcon,
    color: '#A855F7',
    title: (p) => 'New media uploaded',
    desc: (p) => `${p.uploader_name || 'Someone'} uploaded ${p.type || 'media'}`,
  },
};

function timeAgo(iso) {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch (e) {
    return '';
  }
}

const NotificationBell = ({ notifications, unreadCount, connected, markAllRead, clearAll }) => {
  const handleOpenChange = (open) => {
    if (open && unreadCount > 0) {
      markAllRead();
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="realtime-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-6 h-6" style={{ color: '#0F172A' }} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full text-white"
              style={{ backgroundColor: '#EF4444' }}
              data-testid="realtime-unread-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          <span
            className="absolute bottom-1 right-1 w-2 h-2 rounded-full ring-2 ring-white"
            style={{ backgroundColor: connected ? '#22C55E' : '#94A3B8' }}
            title={connected ? 'Live' : 'Reconnecting...'}
            data-testid="realtime-connection-dot"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 overflow-hidden"
        data-testid="realtime-notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: '#0F172A' }}>
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm">Live Activity</span>
            {connected ? (
              <Wifi className="w-4 h-4" style={{ color: '#22C55E' }} data-testid="realtime-status-connected" />
            ) : (
              <WifiOff className="w-4 h-4" style={{ color: '#94A3B8' }} data-testid="realtime-status-disconnected" />
            )}
          </div>
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="text-xs flex items-center gap-1 hover:opacity-80"
              style={{ color: '#D4AF37' }}
              data-testid="realtime-clear-btn"
            >
              <Trash2 className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        <ScrollArea className="max-h-96">
          {notifications.length === 0 ? (
            <div className="py-10 px-4 text-center text-sm text-slate-500" data-testid="realtime-empty-state">
              <CheckCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No live activity yet.<br />New votes and registrations will appear here.
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => {
                const meta = EVENT_META[n.type];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    data-testid={`realtime-notification-item-${n.type}`}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${meta.color}22` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{meta.title(n.payload)}</p>
                      <p className="text-xs text-slate-500 truncate">{meta.desc(n.payload)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
